import React from 'react';
import { FileText, GraduationCap, DollarSign, AlertTriangle, Shield, Phone, Calendar, XCircle, CheckCircle, Clock } from 'lucide-react';
import { Link } from '@/i18n/navigation';
import { useI18n } from '@/i18n';

const TermsOfServiceTutorsPage: React.FC = () => {
  const { locale } = useI18n();

  if (locale === 'zh-CN') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-secondary py-16 text-white">
          <div className="container mx-auto px-4">
            <div className="mb-4 flex items-center justify-center">
              <GraduationCap className="mr-4 h-12 w-12" />
              <h1 className="text-4xl font-bold md:text-5xl">服务条款</h1>
            </div>
            <p className="mb-2 text-center text-2xl font-semibold text-accent">适用于导师</p>
            <p className="mx-auto max-w-3xl text-center text-gray-200">生效日期：2026 年 4 月 23 日</p>
            <p className="mx-auto mt-2 max-w-3xl text-center text-gray-200">最后更新：2026 年 4 月 23 日</p>
          </div>
        </div>

        <div className="container mx-auto max-w-5xl px-4 py-12">
          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <GraduationCap className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-secondary" />
              <div className="text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-secondary">1. 平台概览</h2>
                <div className="space-y-4">
                  <p>
                    Tutor Founder 是马来西亚的补习配对平台，连接合资格导师与家长/学生，并在前期付款阶段提供托管安排。
                  </p>
                  <p>
                    作为导师，你是独立承包者，并非 Tutor Founder 的雇员。你需自行负责教学服务、税务义务及专业行为。
                  </p>
                  <p className="font-semibold text-secondary">注册或继续使用平台，即表示你同意本条款、隐私政策及适用法律法规。</p>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <Shield className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-secondary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-secondary">2. 服务内容</h2>
                <ul className="ml-6 list-disc space-y-2">
                  <li>建立导师档案并向潜在学生展示</li>
                  <li>根据科目、地区、经验与偏好进行配对</li>
                  <li>提供接案通知、付款收集与月度拨款安排</li>
                  <li>协助与家长/学生的沟通，以及在适用情况下提供争议协调支持</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <CheckCircle className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-secondary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-secondary">3. 导师注册与验证</h2>
                <ul className="ml-6 list-disc space-y-2">
                  <li>你必须年满 18 岁，并提供真实、完整且最新的个人资料</li>
                  <li>你可能需要提交身份证件、学历证明、教学证书与银行户口资料</li>
                  <li>平台有权核验你的身份与资格；若提供虚假资料，账户可被立即终止</li>
                  <li>你需自行维护资料准确性，并妥善保管账户与密码</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <Calendar className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-secondary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-secondary">4. 接受教学个案</h2>
                <ul className="ml-6 list-disc space-y-2">
                  <li>你会通过平台收到家长/学生的教学请求，可依据自己的时间与偏好接受或拒绝</li>
                  <li>一旦你通过电话向 Tutor Founder 确认接受个案，即视为具约束力的承诺</li>
                  <li>你需与客户协调上课时间、地点与相关安排，并遵守已确认的课程计划</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <Clock className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-secondary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-secondary">5. 课程验证与拨款</h2>
                <ul className="ml-6 list-disc space-y-2">
                  <li>每次课程后，Tutor Founder 会通过 WhatsApp 向客户确认出席情况</li>
                  <li>若客户在 24 小时内未回复，课时会自动视为已完成，避免拨款延迟</li>
                  <li>款项会按每月底的月度拨款周期处理</li>
                  <li>课程安排、验证与行政协调必须通过官方 WhatsApp 渠道进行</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <DollarSign className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-secondary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-secondary">6. 收费与付款结构</h2>
                <ul className="ml-6 list-disc space-y-2">
                  <li>第 1-2 个月，客户向 Tutor Founder 付款，平台收取 25% 行政佣金，其余 75% 拨付导师</li>
                  <li>第 3 个月起，客户与导师直接结算，平台佣金为 0%</li>
                  <li>在第 1-2 个月内，你不得要求或接受客户绕过平台直接付款</li>
                  <li>款项会汇入你登记的马来西亚银行户口，户口资料需保持正确并与法定姓名一致</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <AlertTriangle className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-secondary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-secondary">7. 专业行为与责任</h2>
                <ul className="ml-6 list-disc space-y-2">
                  <li>你应提供符合专业标准的补习服务，准时可靠，并准备合适的教材与活动</li>
                  <li>必须尊重家长与学生，并保护学生资料与学习进度的机密性</li>
                  <li>不得进行不当、违法、骚扰、误导或规避平台收费的行为</li>
                  <li>若课程在客户家中进行，你需尊重对方住家规则；若在你自己的场地进行，你需确保环境安全</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <FileText className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-secondary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-secondary">8. 独立承包者身份</h2>
                <ul className="ml-6 list-disc space-y-2">
                  <li>你与 Tutor Founder 之间不存在雇佣、合伙或代理关系</li>
                  <li>你需自行处理税务、保险及其他法定义务</li>
                  <li>你不享有员工福利，例如 EPF、SOCSO、医疗保险或有薪假期</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <AlertTriangle className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-secondary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-secondary">9. 平台责任限制</h2>
                <ul className="ml-6 list-disc space-y-2">
                  <li>Tutor Founder 是中介平台，不直接监督或控制你的教学内容与行为</li>
                  <li>平台不对你与客户之间的争议、冲突、法律问题或安全事故承担直接责任</li>
                  <li>平台不保证你一定会接到个案或获得收入</li>
                  <li>在法律允许范围内，平台可依据条款限制其责任范围</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <Phone className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-secondary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-secondary">10. 争议处理</h2>
                <ul className="ml-6 list-disc space-y-2">
                  <li>若与客户有争议，应先尝试直接沟通解决</li>
                  <li>如无法解决，可请求 Tutor Founder 协助调解</li>
                  <li>正式投诉应在事件发生后 14 天内书面提交</li>
                  <li>本条款受马来西亚法律管辖，并由马来西亚法院专属管辖</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <Shield className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-secondary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-secondary">11. 隐私与资料保护</h2>
                <p>
                  你使用 Tutor Founder 时，也同时受我们的 <Link to="/privacy-policy" className="font-semibold text-accent hover:underline">隐私政策</Link> 约束。该政策说明我们如何收集、使用并保护导师的身份资料、学历证明、银行资料及其他个人信息。
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <XCircle className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-secondary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-secondary">12. 账户暂停与终止</h2>
                <ul className="ml-6 list-disc space-y-2">
                  <li>你可在任何时候通知 Tutor Founder 以停用账户</li>
                  <li>若违反条款、提供虚假资料、严重失职、频繁爽约或规避平台收费，平台可暂停或终止账户</li>
                  <li>终止账户并不当然免除你在终止前已产生的责任与义务</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="rounded-lg bg-secondary p-8 text-white shadow-md">
            <h2 className="mb-4 text-2xl font-bold">联络与同意</h2>
            <div className="space-y-4">
              <p>若你对本条款有疑问，可联络 Tutor Founder：tutorfounder@gmail.com / +60 11-12049396。</p>
              <p className="font-semibold">继续注册或使用平台，即表示你已阅读、理解并同意受本服务条款约束。</p>
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-secondary text-white py-16">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center mb-4">
            <GraduationCap className="h-12 w-12 mr-4" />
            <h1 className="text-4xl md:text-5xl font-bold">Terms of Service</h1>
          </div>
          <p className="text-center text-2xl font-semibold text-accent mb-2">
            For Tutors
          </p>
          <p className="text-center text-gray-200 max-w-3xl mx-auto">
            Effective Date: April 23, 2026
          </p>
          <p className="text-center text-gray-200 max-w-3xl mx-auto mt-2">
            Last Updated: April 23, 2026
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12 max-w-5xl">

        {/* Introduction */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <GraduationCap className="h-6 w-6 text-secondary mr-3 mt-1 flex-shrink-0" />
            <div>
              <h2 className="text-2xl font-bold text-secondary mb-4">1. Platform Overview</h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  <strong>Tutor Founder</strong> operates as a dedicated tuition matching platform for academic and music education in Malaysia. We connect qualified tutors with students and facilitate secure payments via an escrow model.
                </p>
                <p>
                  <strong>Important Note:</strong> You are an independent contractor, not an employee of Tutor Founder. You are responsible for your own tutoring services, tax obligations, and professional conduct.
                </p>
                <p>
                  By registering, accessing, or using Tutor Founder as a tutor, you acknowledge that you have read, understood, and agree to be bound by these Terms, our Privacy Policy, and all applicable laws and regulations of Malaysia.
                </p>
                <p className="font-semibold text-secondary">
                  If you do not agree to these Terms, you must not register as a tutor or use our platform.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Service Description */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <Shield className="h-6 w-6 text-secondary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-secondary mb-4">2. Service Description</h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  Tutor Founder is an online platform that connects qualified private tutors with parents and students in Malaysia. Our services for tutors include:
                </p>
                <ul className="list-disc ml-6 space-y-2">
                  <li>Tutor profile creation and visibility to potential students</li>
                  <li>Matching with students based on subjects, location, experience, and preferences</li>
                  <li>Booking and lesson request notifications</li>
                  <li>Secure payment collection and monthly disbursement</li>
                  <li>Communication facilitation with parents and students</li>
                  <li>Customer support and dispute mediation assistance</li>
                </ul>
                <p className="font-semibold text-secondary mt-4">
                  Important: You are an independent contractor, not an employee of Tutor Founder. You are responsible for your own tutoring services, tax obligations, and professional conduct.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Tutor Registration and Verification */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <CheckCircle className="h-6 w-6 text-secondary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-secondary mb-4">3. Tutor Registration and Verification</h2>
              <div className="text-gray-700 space-y-4">

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">3.1 Registration Requirements</h3>
                  <p className="mb-2">To register as a tutor, you must:</p>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Be at least 18 years of age</li>
                    <li>Provide accurate and complete personal information (full name, contact details, address)</li>
                    <li>Provide a valid <strong>Malaysian Identity Card (IC) number</strong> or <strong>Passport details</strong> for identity verification</li>
                    <li>Provide educational qualifications and certifications (degrees, diplomas, teaching certificates, etc.)</li>
                    <li>Provide bank account details for payment disbursement</li>
                    <li>Upload a professional profile photo</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">3.2 Identity Verification Process</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Tutor Founder will verify your IC number or passport details to confirm your identity</li>
                    <li>You may be required to submit copies of identification documents and educational certificates</li>
                    <li>Your profile will only be activated after successful verification</li>
                    <li>Providing false or misleading information may result in immediate account termination and legal action</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">3.3 Profile Accuracy and Updates</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>You must maintain an accurate and up-to-date tutor profile</li>
                    <li>You must promptly update your profile if your qualifications, availability, or contact information changes</li>
                    <li>False or exaggerated claims about your qualifications or experience are strictly prohibited</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">3.4 Account Security</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>You are responsible for maintaining the confidentiality of your account credentials</li>
                    <li>You must <strong>NOT share your password</strong> with any third party</li>
                    <li>You must notify Tutor Founder immediately of any unauthorized access or security breach</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Accepting Tutoring Assignments */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <Calendar className="h-6 w-6 text-secondary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-secondary mb-4">4. Accepting Tutoring Assignments</h2>
              <div className="text-gray-700 space-y-4">

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">4.1 Booking Requests</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>You will receive booking requests from parents/students through the platform</li>
                    <li>You have the right to accept or decline booking requests based on your availability and preferences</li>
                    <li>You should respond to booking requests promptly (within 24 hours)</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">4.2 Confirmation by Phone</h3>
                  <p className="mb-2 font-semibold text-secondary">
                    Once you confirm acceptance of a tutoring assignment with Tutor Founder by phone, you are legally bound to fulfill the assignment.
                  </p>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Verbal phone confirmation is considered a binding commitment</li>
                    <li>You must honor your commitment unless extraordinary circumstances arise (see Section 5.2)</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">4.3 Lesson Scheduling</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>You and the customer must agree on lesson schedules, locations, and any special requirements</li>
                    <li>You are responsible for coordinating lesson logistics directly with the customer</li>
                    <li>You must adhere to the agreed lesson schedule and duration</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">4.4 Additional Lessons in Same Month</h3>
                  <p>
                    If additional lessons are conducted within the same month beyond the originally agreed number, <strong>no extra fees will be charged to the customer</strong>. You will still receive payment based on the original monthly agreement.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Lesson Verification and Payout */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <Clock className="h-6 w-6 text-secondary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-secondary mb-4">5. Lesson Verification & Payout</h2>
              <div className="text-gray-700 space-y-4">

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">5.1 Attendance Verification</h3>
                  <p className="mb-2">
                    Following each scheduled lesson, <strong>Tutor Founder will verify attendance with the client via WhatsApp.</strong>
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">5.2 24-Hour Auto-Complete</h3>
                  <p className="mb-2">
                    If a client does not reply to the WhatsApp verification prompt within <strong>24 hours</strong>, the system automatically defaults the lesson status to <strong>"Completed"</strong> to prevent payment delays for you.
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">5.3 Monthly Payout Schedule</h3>
                  <p className="mb-2">
                    Upon verification or auto-completion, your payment will be processed on a <strong>monthly disbursement schedule at the end of each month.</strong>
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">5.4 Official Communication</h3>
                  <p className="mb-2">
                    All scheduling, attendance verification, and administrative coordination must be conducted via <strong>Tutor Founder's official WhatsApp channels.</strong> Do not coordinate payments outside the platform.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Billing and Payment Structure */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <DollarSign className="h-6 w-6 text-secondary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-secondary mb-4">6. Billing & Payment Structure</h2>
              <div className="text-gray-700 space-y-4">

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">6.1 Platform Billing (Months 1 & 2)</h3>
                  <p className="mb-2">
                    Clients pay a fixed monthly tuition directly to Tutor Founder at the end of each month. The platform deducts a <strong>25% administrative commission</strong> and disburses the remaining <strong>75% to you.</strong>
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">6.2 Direct Billing (Month 3 Onward)</h3>
                  <p className="mb-2">
                    Upon successful completion of the two-month introductory period, <strong>clients pay you directly according to mutually agreed terms. Tutor Founder claims 0% commission from Month 3 onward.</strong>
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">6.3 No Direct Payments During Month 1 & 2</h3>
                  <p className="mb-2">
                    You must <strong>NOT</strong> request or accept direct payments from clients during Month 1 and Month 2. All payments must flow through the Tutor Founder platform during this period.
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">6.4 Monthly Disbursement</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Tutor Founder disburses your payments at the <strong>end of each month</strong></li>
                    <li>Payments are transferred to the bank account you provided during registration</li>
                    <li>Disbursements are based on lessons conducted and verified during the month</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">6.5 Bank Account Requirements</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>You must provide valid Malaysian bank account details</li>
                    <li>The bank account must be in your legal name (as per IC or passport)</li>
                    <li>You are responsible for updating your bank account information if it changes</li>
                    <li>Tutor Founder is not liable for payment delays or failures due to incorrect bank account information</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Professional Conduct and Responsibilities */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <AlertTriangle className="h-6 w-6 text-secondary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-secondary mb-4">7. Professional Conduct and Responsibilities</h2>
              <div className="text-gray-700 space-y-4">

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">7.1 Professional Standards</h3>
                  <p className="mb-2">As a tutor, you must:</p>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Provide high-quality tutoring services in accordance with professional standards</li>
                    <li>Be punctual and reliable for scheduled lessons</li>
                    <li>Prepare lesson materials and activities appropriate to the student's level</li>
                    <li>Treat students and parents with respect, professionalism, and courtesy</li>
                    <li>Maintain confidentiality of student information and academic progress</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">7.2 Prohibited Conduct</h3>
                  <p className="mb-2">You must <strong>NOT</strong>:</p>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Engage in any inappropriate, unprofessional, or unlawful conduct with students or parents</li>
                    <li>Request or accept payments outside the Tutor Founder platform to avoid platform fees</li>
                    <li>Use the platform to solicit students for other services or businesses</li>
                    <li>Share your account credentials with any third party</li>
                    <li>Provide false or misleading information about your qualifications or experience</li>
                    <li>Harass, threaten, or abuse customers or Tutor Founder staff</li>
                    <li>Use any device, software, or program to interfere with the normal operation of the platform</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">7.3 Safety and Safeguarding</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>You must ensure the safety and well-being of students during lessons</li>
                    <li>If lessons are conducted at a customer's home, you must adhere to their house rules and respect their privacy</li>
                    <li>If lessons are conducted at your location, you must ensure a safe and conducive learning environment</li>
                    <li>You must report any safeguarding concerns to Tutor Founder immediately</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">7.4 Communication and Responsiveness</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>You must respond promptly to messages from customers and Tutor Founder</li>
                    <li>You must notify customers in advance if you need to reschedule a lesson</li>
                    <li>You must communicate any issues or concerns to Tutor Founder promptly</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">7.5 No-Show Policy</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>If you fail to attend a scheduled lesson without prior notice (at least 24 hours in advance), you will not be paid for that lesson</li>
                    <li>Repeated no-shows may result in account suspension or termination</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Independent Contractor Status */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <FileText className="h-6 w-6 text-secondary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-secondary mb-4">8. Independent Contractor Status</h2>
              <div className="text-gray-700 space-y-4">

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">8.1 No Employment Relationship</h3>
                  <p>
                    You are an <strong>independent contractor</strong>, not an employee of Tutor Founder. Nothing in these Terms creates an employment, partnership, or agency relationship between you and Tutor Founder.
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">8.2 Tax Obligations</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>You are responsible for your own tax obligations, including income tax, GST/SST (if applicable), and any other taxes</li>
                    <li>Tutor Founder does not withhold taxes from your payments</li>
                    <li>You must comply with all applicable Malaysian tax laws and regulations</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">8.3 No Benefits</h3>
                  <p>
                    As an independent contractor, you are not entitled to employee benefits such as EPF, SOCSO, health insurance, or paid leave.
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">8.4 Professional Liability and Insurance</h3>
                  <p>
                    You are responsible for obtaining any necessary professional liability insurance or other insurance coverage. Tutor Founder does not provide insurance coverage for tutors.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Platform Liability and Disclaimers */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <AlertTriangle className="h-6 w-6 text-secondary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-secondary mb-4">9. Platform Liability and Disclaimers</h2>
              <div className="text-gray-700 space-y-4">

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">9.1 Intermediary Role</h3>
                  <p>
                    Tutor Founder acts solely as an intermediary platform to facilitate connections between tutors and customers. We do not supervise, direct, or control your tutoring services.
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">9.2 No Liability for Tutor Conduct</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li><strong>Tutor Founder is not liable</strong> for your conduct, performance, or any incidents that arise during lessons</li>
                    <li>We are not responsible for disputes, conflicts, or legal issues between you and customers</li>
                    <li>You are solely responsible for your actions and decisions as an independent tutor</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">9.3 No Liability for Unreasonable Customer Requests</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Tutor Founder is not liable for unreasonable requests made by customers</li>
                    <li>You have the right to decline unreasonable or inappropriate requests</li>
                    <li>If you experience unreasonable customer behavior, you should report it to Tutor Founder</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">9.4 No Guarantee of Bookings</h3>
                  <p>
                    Tutor Founder does not guarantee that you will receive booking requests or generate income through the platform. Your success depends on your qualifications, experience, availability, and customer demand.
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">9.5 Limitation of Liability</h3>
                  <p className="mb-2">
                    To the fullest extent permitted by Malaysian law, Tutor Founder shall not be liable for:
                  </p>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Any indirect, incidental, special, consequential, or punitive damages</li>
                    <li>Loss of income, revenue, or business opportunities</li>
                    <li>Damages arising from customer conduct, disputes, or safety incidents</li>
                    <li>Payment disputes or delays caused by customer non-payment</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">9.6 Indemnification</h3>
                  <p>
                    You agree to indemnify and hold Tutor Founder harmless from any claims, damages, liabilities, or expenses arising out of your tutoring services, your breach of these Terms, or your violation of any laws or regulations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Dispute Resolution */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <Phone className="h-6 w-6 text-secondary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-secondary mb-4">10. Dispute Resolution</h2>
              <div className="text-gray-700 space-y-4">

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">10.1 Internal Resolution Process</h3>
                  <p>
                    If you have a dispute with a customer (regarding lesson expectations, payment, conduct, etc.), you should first attempt to resolve it directly with the customer.
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">10.2 Mediation Assistance</h3>
                  <p>
                    If you are unable to resolve the dispute directly, you may contact Tutor Founder for mediation assistance. We will make reasonable efforts to facilitate a resolution.
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">10.3 Formal Complaints</h3>
                  <p>
                    Formal complaints must be submitted in writing to Tutor Founder within <strong>14 days</strong> of the incident. We will investigate and respond within a reasonable timeframe.
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">10.4 Governing Law and Jurisdiction</h3>
                  <p>
                    These Terms shall be governed by and construed in accordance with the laws of Malaysia. Any legal disputes shall be subject to the exclusive jurisdiction of the courts of Malaysia.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Privacy and Data Protection */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <Shield className="h-6 w-6 text-secondary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-secondary mb-4">11. Privacy and Data Protection</h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  Your use of Tutor Founder is also governed by our <Link to="/privacy-policy" className="text-accent hover:underline font-semibold">Privacy Policy</Link>, which explains how we collect, use, and protect your personal information in compliance with the Personal Data Protection Act 2010 (PDPA) of Malaysia.
                </p>
                <p>
                  Key points for tutors:
                </p>
                <ul className="list-disc ml-6 space-y-2">
                  <li>We collect IC/passport details, educational qualifications, and bank account information for verification and payment</li>
                  <li>We share limited information (profile, contact details) with customers to facilitate lesson arrangements</li>
                  <li>We use secure payment processing and data encryption</li>
                  <li>You have rights to access, correct, and request deletion of your personal data</li>
                  <li>Financial records are retained for 7 years in compliance with Malaysian tax regulations</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Account Suspension and Termination */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <XCircle className="h-6 w-6 text-secondary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-secondary mb-4">12. Account Suspension and Termination</h2>
              <div className="text-gray-700 space-y-4">

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">12.1 Termination by Tutor</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>You may deactivate your account at any time by notifying Tutor Founder</li>
                    <li>You must fulfill all existing lesson commitments before deactivating your account</li>
                    <li>All lesson disbursements due to you will be processed according to the commission structure outlined in Section 6</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">12.2 Suspension or Termination by Tutor Founder</h3>
                  <p className="mb-2">Tutor Founder may suspend or terminate your account if:</p>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>You violate these Terms</li>
                    <li>You engage in fraudulent, illegal, or unprofessional conduct</li>
                    <li>You repeatedly cancel assignments or fail to attend lessons</li>
                    <li>You receive multiple customer complaints</li>
                    <li>You provide false information during registration or verification</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">12.3 Effect of Termination</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Upon termination, your profile will be removed from the platform</li>
                    <li>You will receive any outstanding payments owed to you (subject to deductions for fees or penalties)</li>
                    <li>You must cease all use of the Tutor Founder platform and branding</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Modifications to Terms */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <FileText className="h-6 w-6 text-secondary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-secondary mb-4">13. Modifications to Terms</h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  Tutor Founder reserves the right to modify these Terms at any time. If we make material changes, we will post a prominent notice on our platform.
                </p>
                <p>
                  Your continued use of Tutor Founder after such modifications constitutes your acceptance of the updated Terms.
                </p>
                <p className="font-semibold text-secondary">
                  We encourage you to review these Terms periodically.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Information */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <Phone className="h-6 w-6 text-secondary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-secondary mb-4">14. Contact Information</h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  If you have any questions, concerns, or complaints regarding these Terms, please contact us:
                </p>

                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <p className="font-semibold text-secondary mb-3">Tutor Founder Tutor Support</p>
                  <div className="space-y-2">
                    <p><strong>Address:</strong> Kuala Lumpur, Malaysia</p>
                    <p><strong>Email:</strong> <a href="mailto:tutorfounder@gmail.com" className="text-accent hover:underline">tutorfounder.com</a></p>
                    <p><strong>Phone:</strong> <a href="tel:+601112049396" className="text-accent hover:underline">+60 11-12049396</a></p>
                    <p><strong>Operating Hours:</strong> Monday - Friday, 9:00 AM - 6:00 PM (GMT+8)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Acknowledgment */}
        <section className="bg-secondary text-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold mb-4">15. Acknowledgment and Consent</h2>
          <div className="space-y-4">
            <p>
              By registering and using Tutor Founder as a tutor, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
            <p>
              You understand that you are an independent contractor and are responsible for your own tutoring services, tax obligations, and professional conduct.
            </p>
            <p className="font-semibold">
              If you do not agree with any part of these Terms, you must discontinue use of our platform immediately.
            </p>
          </div>
        </section>

        {/* Footer Note */}
        <div className="text-center text-gray-600 mt-12 pt-8 border-t border-gray-200">
          <p className="text-sm">
            Thank you for being part of Tutor Founder. We are committed to supporting tutors and providing you with the best platform for connecting with students in Malaysia.
          </p>
        </div>

      </div>
    </div>
  );
};

export default TermsOfServiceTutorsPage;
